import React, { useMemo } from "react";
import { gql } from "graphql.macro";
import Log from "../components/Log";
import { useApolloClient, useQuery } from "react-apollo";
import { useArticleType } from "../../core/context/applicationContext";
import { useRegistry } from "../../plugins/registry";

const GET_LOG = gql`
  query GetLog($type: ArticleType, $first: Int) {
    log(articleType: $type, first: $first) {
      id
      date
      type
      employee
    }
  }
`;

const LOG_CACHE_MAX_AGE = 60 * 60 * 1000;
const logCache = Object.create(null);

function mergeLogItems(newItems, existingItems) {
  const items = [];
  const ids = new Set();

  for (const item of [...newItems, ...existingItems]) {
    if (!ids.has(item.id)) {
      ids.add(item.id);
      items.push(item);
    }
  }

  return items;
}

export default function LogContainer() {
  const registry = useRegistry();
  const [type] = useArticleType();
  const client = useApolloClient();
  const cacheKey = type || "__all__";
  const cacheEntry = logCache[cacheKey];
  const hasFreshCache =
    cacheEntry && Date.now() - cacheEntry.loadedAt < LOG_CACHE_MAX_AGE;
  const [, forceCacheRefresh] = React.useState(0);
  const [refreshImmediately, setRefreshImmediately] = React.useState(
    Boolean(hasFreshCache)
  );
  const [items, setItems] = React.useState(
    hasFreshCache ? cacheEntry.items : []
  );
  const [initialLoadComplete, setInitialLoadComplete] = React.useState(
    Boolean(hasFreshCache)
  );

  React.useEffect(() => {
    if (!hasFreshCache) {
      return undefined;
    }

    const timeout = setTimeout(
      () => forceCacheRefresh((version) => version + 1),
      Math.max(cacheEntry.loadedAt + LOG_CACHE_MAX_AGE - Date.now(), 0) + 1
    );
    return () => clearTimeout(timeout);
  }, [cacheEntry, hasFreshCache]);

  const query = useMemo(() => {
    const selections = [
      ...GET_LOG.definitions[0].selectionSet.selections[0].selectionSet
        .selections,
    ];
    for (const { logItemFragment } of Object.values(registry.logItemTypes)) {
      selections.push({
        kind: "InlineFragment",
        directives: [],
        selectionSet: logItemFragment.definitions[0].selectionSet,
        typeCondition: logItemFragment.definitions[0].typeCondition,
      });
    }
    return {
      ...GET_LOG,
      definitions: [
        {
          ...GET_LOG.definitions[0],
          selectionSet: {
            ...GET_LOG.definitions[0].selectionSet,
            selections: [
              {
                ...GET_LOG.definitions[0].selectionSet.selections[0],
                selectionSet: {
                  ...GET_LOG.definitions[0].selectionSet.selections[0]
                    .selectionSet,
                  selections,
                },
              },
            ],
          },
        },
      ],
    };
  }, [registry]);

  const { data, error, loading } = useQuery(query, {
    variables: { type },
    fetchPolicy: "network-only",
    skip: hasFreshCache,
  });

  React.useEffect(() => {
    const currentCacheEntry = logCache[cacheKey];
    const currentCacheIsFresh =
      currentCacheEntry &&
      Date.now() - currentCacheEntry.loadedAt < LOG_CACHE_MAX_AGE;
    setRefreshImmediately(Boolean(currentCacheIsFresh));
  }, [cacheKey, query, type]);

  React.useEffect(() => {
    if (!hasFreshCache && cacheEntry) {
      setRefreshImmediately(false);
    }
    if (hasFreshCache) {
      setItems(cacheEntry.items);
      setInitialLoadComplete(true);
    } else {
      setItems([]);
      setInitialLoadComplete(false);
    }
  }, [cacheEntry, hasFreshCache, query, type]);

  React.useEffect(() => {
    if (!hasFreshCache && !loading && data && data.log) {
      setItems(data.log);
      logCache[cacheKey] = {
        items: data.log,
        loadedAt: Date.now(),
      };
      setInitialLoadComplete(true);
    }
  }, [cacheKey, data, hasFreshCache, loading]);

  React.useEffect(() => {
    if (!initialLoadComplete) {
      return undefined;
    }

    let cancelled = false;
    const loadLatestItems = () => {
      client
        .query({
          query,
          variables: { type, first: 200 },
          fetchPolicy: "network-only",
        })
        .then(({ data: latestData }) => {
          if (!cancelled && latestData && latestData.log) {
            setItems((existingItems) => {
              const mergedItems = mergeLogItems(
                latestData.log,
                existingItems
              );
              logCache[cacheKey] = {
                items: mergedItems,
                loadedAt: logCache[cacheKey].loadedAt,
              };
              return mergedItems;
            });
          }
        })
        .catch(() => undefined);
    };

    if (refreshImmediately && hasFreshCache) {
      loadLatestItems();
    }
    const interval = setInterval(loadLatestItems, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    cacheKey,
    client,
    hasFreshCache,
    initialLoadComplete,
    query,
    refreshImmediately,
    type,
  ]);

  return (
    <Log
      items={items}
      articleType={type}
      error={error}
      loading={loading && !initialLoadComplete}
    />
  );
}
